import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { KeyRound } from "lucide-react";

export default function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Please sign in again.");
      logout();
      nav("/login", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="change-password-page" className="max-w-lg">
      <div className="eyebrow">Security</div>
      <h1 className="mt-2 text-4xl">Change your password</h1>
      <p className="mt-3 text-[hsl(var(--ink-soft))]">
        Signed in as <span className="text-[hsl(var(--ink))] font-semibold">{user?.email}</span>.
        You'll be signed out after a successful change and asked to sign in again.
      </p>

      <form data-testid="change-password-form" onSubmit={submit} className="mt-8 space-y-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-6">
        <Field label="Current password">
          <input required type="password" data-testid="cp-current" value={current} onChange={(e) => setCurrent(e.target.value)} className="cp-inp" />
        </Field>
        <Field label="New password (min 8 characters)">
          <input required type="password" data-testid="cp-new" value={next} onChange={(e) => setNext(e.target.value)} className="cp-inp" />
        </Field>
        <Field label="Confirm new password">
          <input required type="password" data-testid="cp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="cp-inp" />
        </Field>
        <button data-testid="cp-submit" type="submit" disabled={busy} className="btn-brass disabled:opacity-60">
          <KeyRound size={14} /> {busy ? "Updating..." : "Update password"}
        </button>
      </form>

      <style>{`
        .cp-inp {
          width: 100%;
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.75rem 0.9rem;
          font-family: inherit;
          font-size: 0.95rem;
          color: hsl(var(--ink));
        }
        .cp-inp:focus { outline: none; border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))] mb-2">{label}</span>
      {children}
    </label>
  );
}
